import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, AlertTriangle, Search, Eye, X, Image, Send, CheckCheck, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API}${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  return res.json();
};

const statusConfig = {
  pending:  { cls: 'badge-amber', icon: Clock,         label: 'Pending'  },
  accepted: { cls: 'badge-green', icon: CheckCircle,   label: 'Accepted' },
  damaged:  { cls: 'badge-red',   icon: AlertTriangle, label: 'Damaged'  },
};

// ── Inline Damage Report Page ────────────────────────────────────────────────
function DamageReportPage({ record, onBack }) {
  const [imageError,  setImageError]  = useState({});
  const [replyMsg,    setReplyMsg]    = useState('');
  const [sending,     setSending]     = useState(false);
  const [sendResult,  setSendResult]  = useState(null); // { ok, text }

  let images = [];
  try {
    if (Array.isArray(record.damage_images)) {
      images = record.damage_images;
    } else if (typeof record.damage_images === 'string') {
      images = JSON.parse(record.damage_images || '[]');
    }
  } catch (e) {
    images = [];
  }

  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
    if (imagePath.startsWith('data:image')) return imagePath;
    const baseUrl = API.replace('/api', '');
    let cleanPath = imagePath;
    if (cleanPath.startsWith('/uploads/') || cleanPath.startsWith('uploads/')) {
      cleanPath = cleanPath.startsWith('/') ? cleanPath : '/' + cleanPath;
    } else if (!cleanPath.startsWith('/')) {
      cleanPath = '/uploads/damage/' + cleanPath;
    }
    return `${baseUrl}${cleanPath}`;
  };

  const openImageInNewTab = async (imageUrl) => {
    if (imageUrl.startsWith('data:image')) {
      const w = window.open();
      w.document.write(`<img src="${imageUrl}" style="max-width:100%;height:auto;"/>`);
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const resp = await fetch(imageUrl, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error();
      const blob    = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const w       = window.open(blobUrl, '_blank');
      if (!w) window.location.href = blobUrl;
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      const token = localStorage.getItem('token');
      window.open(`${imageUrl}?token=${encodeURIComponent(token)}`, '_blank');
    }
  };

  const handleSendReply = async () => {
    if (!replyMsg.trim()) return;
    setSending(true);
    setSendResult(null);
    try {
      const data = await apiFetch(`/acceptance/${record.id}/reply`, {
        method: 'POST',
        body: JSON.stringify({ message: replyMsg.trim() }),
      });
      if (data.success) {
        setSendResult({ ok: true, text: `Reply sent to ${record.emp_email}` });
        setReplyMsg('');
      } else {
        setSendResult({ ok: false, text: data.message || 'Failed to send. Please try again.' });
      }
    } catch {
      setSendResult({ ok: false, text: 'Network error. Please try again.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fade-in">
      {/* ── Page header ── */}
      <div className="page-header page-header-row" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button
            className="btn btn-secondary"
            onClick={onBack}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px' }}
          >
            <ArrowLeft size={14}/> Back
          </button>
          <div>
            <h1 style={{ margin: 0 }}>Damage Report</h1>
            <p style={{ margin: 0 }}>
              Asset&nbsp;
              <span style={{ fontFamily: 'var(--mono)', color: 'var(--red)', fontWeight: 700 }}>
                {record.asset_id}
              </span>
              {record.brand && (
                <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: 13 }}>
                  — {record.brand} {record.model}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* ── Employee info card ── */}
        <div className="card" style={{ padding: '20px 22px' }}>
          <div className="section-title" style={{ marginBottom: 12 }}>Employee</div>
          <div className="info-row"><span className="info-label">Name</span><span className="info-value">{record.emp_name}</span></div>
          <div className="info-row"><span className="info-label">Email</span><span className="info-value">{record.emp_email}</span></div>
          <div className="info-row">
            <span className="info-label">Asset</span>
            <span className="info-value" style={{ fontFamily: 'var(--mono)', color: 'var(--red)', fontWeight: 700 }}>{record.asset_id}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Submitted</span>
            <span className="info-value" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
              {record.submitted_at ? new Date(record.submitted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
            </span>
          </div>
        </div>

        {/* ── Damage description card ── */}
        <div className="card" style={{ padding: '20px 22px' }}>
          <div className="section-title" style={{ marginBottom: 12 }}>
            <AlertTriangle size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle', color: 'var(--red)' }}/>
            Damage Description
          </div>
          <div style={{
            background: 'var(--red-bg)',
            border: '1px solid rgba(248,113,113,.2)',
            borderRadius: 'var(--radius)',
            padding: '12px 14px',
            fontSize: 13.5,
            color: 'var(--text)',
            lineHeight: 1.75,
            minHeight: 72,
          }}>
            {record.damage_desc || '—'}
          </div>
        </div>
      </div>

      {/* ── Damage photos ── */}
      {images.length > 0 && (
        <div className="card" style={{ padding: '20px 22px', marginBottom: 20 }}>
          <div className="section-title" style={{ marginBottom: 14 }}>
            <Image size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }}/>
            Damage Photos ({images.length})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
            {images.map((src, i) => {
              const fullUrl = getFullImageUrl(src);
              return (
                <div
                  key={i}
                  style={{
                    aspectRatio: '1',
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid var(--border)',
                    background: '#f5f5f5',
                    cursor: 'pointer',
                    position: 'relative',
                  }}
                  onClick={() => openImageInNewTab(fullUrl)}
                >
                  {!imageError[i] ? (
                    <img
                      src={fullUrl}
                      alt={`Damage ${i + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      onError={() => setImageError(prev => ({ ...prev, [i]: true }))}
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', color: '#999' }}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                      <p style={{ fontSize: 11, marginTop: 5 }}>Click to view</p>
                    </div>
                  )}
                  <div style={{
                    position: 'absolute', bottom: 6, right: 6,
                    background: 'rgba(0,0,0,0.55)', color: 'white',
                    padding: '3px 7px', borderRadius: 4, fontSize: 10, pointerEvents: 'none',
                  }}>
                    🔍 Enlarge
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Reply to employee ── */}
      <div className="card" style={{ padding: '20px 22px' }}>
        <div className="section-title" style={{ marginBottom: 12 }}>
          <Send size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }}/>
          Reply to Employee
        </div>

        {/* Recipient pill */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>To:</span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: 'var(--accent-bg, rgba(99,102,241,0.08))',
            border: '1px solid var(--accent-border, rgba(99,102,241,0.2))',
            borderRadius: 20, padding: '3px 12px',
            fontSize: 12.5, fontWeight: 600, color: 'var(--accent)',
          }}>
            {record.emp_name} &lt;{record.emp_email}&gt;
          </span>
        </div>

        <textarea
          rows={5}
          className="form-input"
          placeholder={`Type your message to ${record.emp_name}…`}
          value={replyMsg}
          onChange={e => { setReplyMsg(e.target.value); setSendResult(null); }}
          disabled={sending}
          style={{
            width: '100%', resize: 'vertical',
            fontFamily: 'inherit', fontSize: 13.5, lineHeight: 1.6,
            padding: '10px 12px', boxSizing: 'border-box',
          }}
        />

        {sendResult && (
          <div style={{
            marginTop: 10, padding: '9px 14px', borderRadius: 'var(--radius)',
            fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 7,
            background: sendResult.ok ? 'rgba(34,197,94,0.08)' : 'rgba(248,113,113,0.08)',
            border: `1px solid ${sendResult.ok ? 'rgba(34,197,94,0.25)' : 'rgba(248,113,113,0.25)'}`,
            color: sendResult.ok ? 'var(--green)' : 'var(--red)',
          }}>
            {sendResult.ok ? <CheckCheck size={14}/> : <AlertTriangle size={14}/>}
            {sendResult.text}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button className="btn btn-secondary" onClick={onBack}>Back to List</button>
          <button
            className="btn btn-primary"
            onClick={handleSendReply}
            disabled={sending || !replyMsg.trim()}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {sending ? (
              <>
                <span style={{
                  width: 12, height: 12,
                  border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff',
                  borderRadius: '50%', display: 'inline-block',
                  animation: 'spin 0.6s linear infinite',
                }}/>
                Sending…
              </>
            ) : (
              <><Send size={13}/> Send Reply</>
            )}
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Main Acceptance Tracker ──────────────────────────────────────────────────
export default function AcceptanceTracker() {
  const [records,     setRecords]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [filter,      setFilter]      = useState('All');
  const [activeRecord, setActiveRecord] = useState(null); // non-null → show DamageReportPage

  useEffect(() => {
    apiFetch('/acceptance')
      .then(d => { if (d.success) setRecords(d.data); })
      .finally(() => setLoading(false));
  }, []);

  // ── Inline damage report view ────────────────────────────────────────────
  if (activeRecord) {
    return <DamageReportPage record={activeRecord} onBack={() => setActiveRecord(null)} />;
  }

  const filtered = records.filter(r => {
    const mf = filter === 'All' || r.status === filter;
    const ms = !search || [r.emp_name, r.emp_email, r.asset_id]
      .some(v => v?.toLowerCase().includes(search.toLowerCase()));
    return mf && ms;
  });

  const counts = {
    pending:  records.filter(r => r.status === 'pending').length,
    accepted: records.filter(r => r.status === 'accepted').length,
    damaged:  records.filter(r => r.status === 'damaged').length,
  };

  return (
    <div className="fade-in">
      <div className="page-header page-header-row">
        <div>
          <h1>Acceptance Tracker</h1>
          <p>Track employee laptop acceptance and damage reports</p>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Total Sent',      val: records.length,  color: 'var(--accent)' },
          { label: 'Pending',         val: counts.pending,  color: 'var(--amber)'  },
          { label: 'Accepted',        val: counts.accepted, color: 'var(--green)'  },
          { label: 'Damage Reported', val: counts.damaged,  color: 'var(--red)'    },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.color, fontFamily: 'var(--mono)', marginBottom: 4 }}>{s.val}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="inv-toolbar">
        <div className="search-bar" style={{ flex: 1, maxWidth: 340 }}>
          <Search size={15}/>
          <input
            className="form-input"
            placeholder="Search employee, asset…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="toggle-group">
          {['All', 'pending', 'accepted', 'damaged'].map(s => (
            <button
              key={s}
              className={`toggle-btn ${filter === s ? 'active' : ''}`}
              onClick={() => setFilter(s)}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Employee</th><th>Asset</th><th>Sent On</th>
              <th>Submitted On</th><th>Status</th><th>Damage</th><th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7}><div className="empty-state"><p>Loading…</p></div></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7}><div className="empty-state"><CheckCircle size={32}/><p>No records found</p></div></td></tr>
            ) : filtered.map(r => {
              const sc = statusConfig[r.status] || statusConfig.pending;
              return (
                <tr key={r.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.emp_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.emp_email}</div>
                  </td>
                  <td>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 12.5, color: 'var(--accent)', fontWeight: 700 }}>{r.asset_id}</span>
                    {r.brand && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.brand} {r.model}</div>}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-muted)' }}>
                    {r.created_at ? new Date(r.created_at).toLocaleDateString('en-CA') : '-'}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text-muted)' }}>
                    {r.submitted_at
                      ? new Date(r.submitted_at).toLocaleDateString('en-CA')
                      : <span style={{ color: 'var(--amber)' }}>Not yet</span>}
                  </td>
                  <td>
                    <span className={`badge ${sc.cls}`}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor', display: 'inline-block', marginRight: 4 }}/>
                      {sc.label}
                    </span>
                  </td>
                  <td>
                    {r.status === 'damaged' ? (
                      <div>
                        <span style={{ color: 'var(--red)', fontSize: 12.5, fontWeight: 600 }}>⚠ Damage reported</span>
                        {Array.isArray(r.damage_images) && r.damage_images.length > 0 && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                            {r.damage_images.length} photo(s)
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td>
                    {r.status === 'damaged' && (
                      <button className="btn btn-sm btn-danger" onClick={() => setActiveRecord(r)}>
                        <Eye size={12}/> View Report
                      </button>
                    )}
                    {r.status === 'accepted' && (
                      <span style={{ color: 'var(--green)', fontSize: 12, fontWeight: 600 }}>✓ Confirmed</span>
                    )}
                    {r.status === 'pending' && (
                      <span style={{ color: 'var(--amber)', fontSize: 12 }}>Awaiting response</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
